import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  Message,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from "@arco-design/web-react";
import {
  IconDelete,
  IconArrowUp,
  IconArrowDown,
  IconPlus,
  IconEdit,
} from "@arco-design/web-react/icon";

import {
  FetchApi,
  PostApi,
  OverwriteApi,
  DeleteApi,
} from "../../Api/AuthenticatedApi";

const { Text } = Typography;

// A stable local key for stage rows so React (and reordering) stay well-behaved.
// Existing stages already have a server id; new rows get a local one.
let localKeySeq = 0;
const nextLocalKey = () => `new-${localKeySeq++}`;

function toRow(stage) {
  return {
    key: stage.id || nextLocalKey(),
    id: stage.id, // undefined for a not-yet-saved stage
    name: stage.name || "",
    is_terminal: !!stage.is_terminal,
    requires_note: !!stage.requires_note,
    default_probability: stage.default_probability == null ? null : stage.default_probability,
    is_won: !!stage.is_won,
  };
}

// Build the stages payload. New rows OMIT id entirely — sending id:"" would crash
// the Go uuid decode (see the optional-UUID normalization rule).
function stagesPayload(rows) {
  return rows
    .filter((r) => r.name.trim())
    .map((r) => {
      const s = {
        name: r.name.trim(),
        is_terminal: r.is_terminal,
        requires_note: r.requires_note,
        is_won: r.is_terminal ? r.is_won : false,
        default_probability:
          r.default_probability === null || r.default_probability === undefined || r.default_probability === ""
            ? null
            : Number(r.default_probability),
      };
      if (r.id) s.id = r.id;
      return s;
    });
}

export default function PipelineSettings() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [pipelines, setPipelines] = useState([]);
  const [refresh, setRefresh] = useState(true);

  // Editor modal state. `editing` is the pipeline being edited (null = closed);
  // an empty object with no id means "new pipeline".
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [stageRows, setStageRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!refresh) return;
    FetchApi(
      getAccessTokenSilently,
      "/api/pipelines/",
      (data) => {
        setPipelines(data || []);
        setRefresh(false);
      },
      () => navigate("/login"),
      () => {
        setRefresh(false);
        Message.error("Failed to load pipelines");
      }
    );
  }, [getAccessTokenSilently, navigate, refresh]);

  const openNew = () => {
    setEditing({});
    setName("");
    setStageRows([toRow({ name: "" }), toRow({ name: "", is_terminal: true })]);
  };

  const openEdit = (p) => {
    setEditing(p);
    setName(p.name || "");
    setStageRows((p.stages || []).map(toRow));
  };

  const closeEditor = () => {
    setEditing(null);
    setSaving(false);
  };

  const addStage = () => setStageRows((rows) => [...rows, toRow({ name: "" })]);

  const updateStage = (idx, patch) =>
    setStageRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeStage = (idx) =>
    setStageRows((rows) => rows.filter((_, i) => i !== idx));

  const moveStage = (idx, delta) =>
    setStageRows((rows) => {
      const next = idx + delta;
      if (next < 0 || next >= rows.length) return rows;
      const copy = [...rows];
      [copy[idx], copy[next]] = [copy[next], copy[idx]];
      return copy;
    });

  const save = () => {
    if (!name.trim()) {
      Message.warning("Give the pipeline a name");
      return;
    }
    if (!stagesPayload(stageRows).length) {
      Message.warning("Add at least one stage");
      return;
    }
    setSaving(true);
    const body = { name: name.trim(), stages: stagesPayload(stageRows) };
    const onOk = () => {
      Message.success("Pipeline saved");
      closeEditor();
      setRefresh(true);
    };
    const onErr = (err) => {
      setSaving(false);
      Message.error((err && err.message) || "Failed to save pipeline");
    };
    if (editing && editing.id) {
      OverwriteApi(getAccessTokenSilently, "/api/pipelines/" + editing.id, onOk, () => navigate("/login"), onErr, body);
    } else {
      PostApi(getAccessTokenSilently, "/api/pipelines/", onOk, () => navigate("/login"), onErr, body);
    }
  };

  const remove = (p) => {
    Modal.confirm({
      title: "Delete pipeline",
      content: `Delete "${p.name}"? This can't be undone.`,
      okButtonProps: { status: "danger" },
      onOk: () =>
        new Promise((resolve) => {
          DeleteApi(
            getAccessTokenSilently,
            "/api/pipelines/" + p.id,
            () => {
              Message.success("Pipeline deleted");
              setRefresh(true);
              resolve();
            },
            () => navigate("/login"),
            () => {
              Message.error("Failed to delete pipeline");
              resolve();
            },
            {}
          );
        }),
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", render: (n) => <Text style={{ fontWeight: 500 }}>{n}</Text> },
    {
      title: "Stages",
      dataIndex: "stages",
      render: (stages) =>
        (stages || []).length ? (
          stages.map((s) => (
            <Tag
              key={s.id}
              size="small"
              color={s.is_terminal ? "gray" : "arcoblue"}
              style={{ marginRight: 4 }}
            >
              {s.name}
            </Tag>
          ))
        ) : (
          <Text type="secondary">No stages</Text>
        ),
    },
    {
      title: "",
      width: 96,
      render: (_, p) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
          <Button size="small" type="text" shape="circle" icon={<IconEdit />} onClick={() => openEdit(p)} />
          <Button size="small" type="text" shape="circle" icon={<IconDelete />} onClick={() => remove(p)} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Text type="secondary">
        A pipeline is a reusable set of ordered stages (like <Text code>Lead → Qualified → Won</Text>) that
        contacts move through in a workflow. Mark end states (Won, Lost) as <b>terminal</b>. For deal
        pipelines, an optional <b>Win %</b> per stage drives the weighted forecast, and <b>Won</b> flags the
        winning terminal stage.
      </Text>

      <Table
        style={{ marginTop: 16 }}
        size="small"
        columns={columns}
        data={pipelines}
        rowKey="id"
        pagination={false}
        border={false}
        noDataElement={<span style={{ color: "var(--color-text-3)" }}>No pipelines yet.</span>}
      />

      <div style={{ marginTop: 16 }}>
        <Button type="primary" icon={<IconPlus />} onClick={openNew}>
          New pipeline
        </Button>
      </div>

      <Modal
        title={editing && editing.id ? "Edit pipeline" : "New pipeline"}
        visible={!!editing}
        onCancel={closeEditor}
        footer={
          <Space>
            <Button onClick={closeEditor}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={save}>
              Save
            </Button>
          </Space>
        }
        style={{ width: 700 }}
        unmountOnExit
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 4 }}>
            <Text style={{ fontWeight: 500 }}>Name</Text>
          </div>
          <Input placeholder="e.g. Sales pipeline" value={name} onChange={setName} />
        </div>

        <div style={{ marginBottom: 4 }}>
          <Text style={{ fontWeight: 500 }}>Stages</Text>
        </div>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <b>Win %</b> (deal pipelines only) is the chance a deal in this stage will eventually close — set it
            higher as stages advance; it drives the weighted forecast. Leave it blank on end stages: mark the
            winning one as <b>Won</b> (counts as 100%); other terminal stages count as 0%.
          </Text>
        </div>
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          {stageRows.map((row, idx) => (
            <div
              key={row.key}
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
                padding: "8px 10px",
                border: "1px solid var(--color-border-2)",
                borderRadius: 6,
                background: "var(--color-fill-1)",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Button
                  size="mini"
                  type="text"
                  shape="circle"
                  icon={<IconArrowUp />}
                  disabled={idx === 0}
                  onClick={() => moveStage(idx, -1)}
                />
                <Button
                  size="mini"
                  type="text"
                  shape="circle"
                  icon={<IconArrowDown />}
                  disabled={idx === stageRows.length - 1}
                  onClick={() => moveStage(idx, 1)}
                />
              </div>
              <Input
                style={{ flex: 1 }}
                placeholder="Stage name"
                value={row.name}
                onChange={(v) => updateStage(idx, { name: v })}
              />
              <Checkbox
                checked={row.is_terminal}
                onChange={(v) => updateStage(idx, { is_terminal: v })}
              >
                Terminal
              </Checkbox>
              <Checkbox
                checked={row.requires_note}
                onChange={(v) => updateStage(idx, { requires_note: v })}
              >
                Needs note
              </Checkbox>
              <InputNumber
                size="small"
                style={{ width: 104 }}
                min={0}
                max={100}
                suffix="%"
                placeholder="Win"
                value={row.default_probability == null ? undefined : row.default_probability}
                onChange={(v) => updateStage(idx, { default_probability: v == null ? null : v })}
              />
              {row.is_terminal && (
                <Checkbox checked={row.is_won} onChange={(v) => updateStage(idx, { is_won: v })}>
                  Won
                </Checkbox>
              )}
              <Button
                size="small"
                type="text"
                shape="circle"
                icon={<IconDelete />}
                onClick={() => removeStage(idx)}
              />
            </div>
          ))}
        </Space>

        <div style={{ marginTop: 10 }}>
          <Button size="small" type="outline" icon={<IconPlus />} onClick={addStage}>
            Add stage
          </Button>
        </div>
      </Modal>
    </div>
  );
}
